import { useCallback, useEffect, useLayoutEffect, useRef } from "react"

const NEAR_END_PX = 80

const getScrollNode = container => {
  if (typeof document === "undefined") return null
  if (container) {
    const overflowY = window.getComputedStyle(container).overflowY
    if (overflowY === "auto" || overflowY === "scroll") return container
  }
  return document.scrollingElement || document.documentElement
}

const isNearEnd = node =>
  node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_END_PX

/**
 * Keeps a reversed (newest-at-bottom) list pinned to the latest item,
 * and preserves scroll when older pages are prepended.
 */
const useReverseListScroll = ({
  enabled = false,
  containerRef,
  itemsKey,
  isLoading = false,
  resetKey,
} = {}) => {
  const prependAdjustRef = useRef(null)
  const stickToEndRef = useRef(true)

  const markPrepend = useCallback(() => {
    const node = getScrollNode(containerRef?.current)
    if (!node) return
    prependAdjustRef.current = node.scrollHeight - node.scrollTop
  }, [containerRef])

  useEffect(() => {
    stickToEndRef.current = true
    prependAdjustRef.current = null
  }, [resetKey])

  useEffect(() => {
    if (!enabled) return undefined
    const node = getScrollNode(containerRef?.current)
    if (!node) return undefined

    const target =
      node === document.documentElement || node === document.body
        ? window
        : node

    const handleScroll = () => {
      const current = getScrollNode(containerRef?.current)
      if (!current) return
      stickToEndRef.current = isNearEnd(current)
    }

    target.addEventListener("scroll", handleScroll, { passive: true })
    return () => target.removeEventListener("scroll", handleScroll)
  }, [containerRef, enabled])

  useLayoutEffect(() => {
    if (!enabled) return
    const node = getScrollNode(containerRef?.current)
    if (!node) return

    if (prependAdjustRef.current != null) {
      node.scrollTop = node.scrollHeight - prependAdjustRef.current
      prependAdjustRef.current = null
      return
    }

    if (stickToEndRef.current && !isLoading) {
      node.scrollTop = node.scrollHeight
    }
  }, [containerRef, enabled, isLoading, itemsKey])

  return { markPrepend }
}

export default useReverseListScroll
